/**
 * Common datepicker initialization
 * Centralizes all datepicker default settings to ensure consistency
 * across the application. Ensures calendar starts on Monday.
 */

import $ from "jquery-import";

/**
 * Initialize datepicker defaults with common settings
 * @param {Object} options - Optional overrides
 * @param {string} options.lang - Language code ('es', 'fr', or default 'en')
 * @param {string} options.yearRange - Year range (default: "1900:2050")
 */
export function initDatepickerDefaults(options = {}) {
  // Check if jQuery UI datepicker is available
  if (typeof $ === "undefined" || !$.datepicker || !$.datepicker.setDefaults) {
    // jQuery UI not loaded yet, try again later
    if (typeof window !== "undefined") {
      setTimeout(() => initDatepickerDefaults(options), 100);
    }
    return;
  }

  let lang = options.lang;
  if (!lang) {
    // Try to detect language from multiple sources
    if (typeof window !== "undefined") {
      // Try settings module
      if (window.settings && window.settings.LANG) {
        lang = window.settings.LANG;
      }
      // Try trans module
      if (!lang && window._ && window._.languageCode) {
        lang = window._.languageCode;
      }
      // Try HTML lang attribute
      if (!lang && window.$) {
        const htmlLang = window.$("html").attr("lang");
        if (htmlLang) lang = htmlLang;
      }
      // Try meta tag
      if (!lang && window.$) {
        const metaLang = window
          .$("meta[http-equiv=content-language]")
          .attr("content");
        if (metaLang) lang = metaLang;
      }
    }
  }

  // Normalize language code (e.g., "es-ES" -> "es", "es_MX" -> "es", "ES" -> "es")
  if (lang) {
    lang = String(lang).split(/[-_]/)[0].toLowerCase();
  }

  const yearRange = options.yearRange || "1900:2050";

  // Debug: log detected language (can be removed in production)
  if (typeof console !== "undefined" && console.log) {
    console.log("[datepicker-init] Detected language:", lang || "default (en)");
  }

  // Build the complete defaults object with all settings at once
  // This ensures all settings are applied together and prevents partial application
  const defaults = {
    changeMonth: true,
    changeYear: true,
    dateFormat: "yy-mm-dd",
    firstDay: 1, // Monday
    yearRange: yearRange,
    showButtonPanel: true,
  };

  // Language-specific settings
  // IMPORTANT: dayNamesMin must always start with Sunday (index 0)
  // jQuery UI uses firstDay to determine which day to display first
  // With firstDay: 1 (Monday), it will show dayNamesMin[1] in the first column
  if (lang === "es") {
    Object.assign(defaults, {
      monthNamesShort: [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      // Day names: [Sunday, Monday, Tuesday, ...] - jQuery UI will use firstDay to show Monday first
      dayNamesMin: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sá"],
      closeText: "Hecho",
      currentText: "Hoy",
      nextText: "Siguiente",
      prevText: "Anterior",
    });
  } else if (lang === "fr") {
    Object.assign(defaults, {
      monthNamesShort: [
        "Jan",
        "Fév",
        "Mar",
        "Avr",
        "Mai",
        "Jun",
        "Jul",
        "Aoû",
        "Sep",
        "Oct",
        "Nov",
        "Déc",
      ],
      // Day names: [Sunday, Monday, Tuesday, ...] - jQuery UI will use firstDay to show Monday first
      dayNamesMin: ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"],
      closeText: "Finir",
      currentText: "Aujourd'hui",
      nextText: "Prochain",
      prevText: "Précédent",
    });
  } else {
    // Default English - day names starting with Sunday
    Object.assign(defaults, {
      dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
      closeText: "Done",
      currentText: "Today",
      nextText: "Next",
      prevText: "Previous",
    });
  }

  // Apply all defaults in a single call
  $.datepicker.setDefaults(defaults);

  // Verify settings were applied (debug only)
  if (typeof console !== "undefined" && console.log) {
    const currentDefaults = $.datepicker._defaults;
    console.log("[datepicker-init] Applied settings:", {
      lang: lang || "default (en)",
      dayNamesMin: currentDefaults.dayNamesMin,
      closeText: currentDefaults.closeText,
      currentText: currentDefaults.currentText,
      nextText: currentDefaults.nextText,
      prevText: currentDefaults.prevText,
    });
  }
}

export default initDatepickerDefaults;

// Make it globally available for inline scripts in templates
if (typeof window !== "undefined") {
  window.initDatepickerDefaults = initDatepickerDefaults;
}
